<script setup lang="ts">
import * as htmlToImage from 'html-to-image'
import VueMultiselect from 'vue-multiselect'
import type { ChartData } from '~/models/statistics'
import type { ConfigAppType } from '~/types/app.type'

withDefaults(
  defineProps<{
    chartsData?: ChartData[]
    loadingChartGeneration?: boolean
  }>(),
  {
    chartsData: () => [],
    loadingChartGeneration: false,
  },
)

const emit = defineEmits(['update:loadingChartGeneration'])

const $config: ConfigAppType = useRuntimeConfig()
const { $rudderstack } = useNuxtApp()

const chartRefs = ref([])
const charts = ref([])
const chartDefaultFontSize = 12
const chartFontSize = ref(chartDefaultFontSize)
const selectedScale = ref('1x')
const allScales = ref(['1x', '1.5x', '2x', '3x', '4x'])
const hideYAxis = ref(false)

function setMarginAndPaddingAccordingToScale(element, scale: number) {
  const sides = ['Top', 'Bottom', 'Left', 'Right']

  sides.forEach((side) => {
    const margin = window?.getComputedStyle(element)[`margin${side}`]
    const padding = window?.getComputedStyle(element)[`padding${side}`]

    if (margin) {
      const [marginValue] = margin.match(/[0-9]+/g)
      if (marginValue !== '0') {
        const [marginUnit] = margin.match(/[a-zA-Z]+/g)
        element.style[`margin${side}`] =
          parseFloat(marginValue) * scale + marginUnit
      }
    }
    if (padding) {
      const [paddingValue] = padding.match(/[0-9]+/g)
      if (paddingValue !== '0') {
        const [paddingUnit] = padding.match(/[a-zA-Z]+/g)
        element.style[`padding${side}`] =
          parseFloat(paddingValue) * scale + paddingUnit
      }
    }
  })
}

function theRef(chartRef, index: number) {
  const currentRef = chartRefs.value.find((ref) => ref.index === index)

  if (currentRef) {
    currentRef.ref = chartRef
  } else {
    chartRefs.value.push({ ref: chartRef, index })
  }
}

function updateChart(i: number) {
  const currentRef = chartRefs.value.find((ref) => ref.index === i)

  if (currentRef) {
    currentRef.ref.chart.update()
  }
}

function captureScreenWithDomToImage(i: number, chartData) {
  emit('update:loadingChartGeneration', true)

  const elementId = `chart-${i}`
  const scale = parseFloat(selectedScale.value)
  const PX = 'px'

  chartFontSize.value = chartDefaultFontSize * scale
  chartData.key++

  setTimeout(() => {
    const chartContainer = document?.getElementById(elementId)
    const chartWatermark: HTMLElement = chartContainer?.querySelector(
      '[data-testid="chart-watermark"]',
    )
    const chartDecoration: SVGElement & { width; height } =
      chartContainer?.querySelector('[data-testid="chart-decoration"]')
    const watermarkP: HTMLElement = chartWatermark?.querySelector('p')
    const watermarkSVG = chartWatermark?.querySelector('svg')
    const watermarkSVGG = chartWatermark?.querySelector('g')
    const titleContainer: HTMLElement = chartContainer?.querySelector(
      '[data-testid="chart-title-container"]',
    )
    const chartTitle: HTMLElement = chartContainer?.querySelector(
      'p[data-testid="chart-title"]',
    )
    const chartDescription: HTMLElement = chartContainer?.querySelector(
      'span[data-testid="chart-description"]',
    )
    const chartLayout: HTMLDivElement =
      chartContainer?.querySelector('.chart-layout')
    const figure = chartContainer?.querySelector('figure')
    const legendItems: HTMLElement[] = [
      ...(chartContainer?.querySelectorAll<HTMLElement>(
        '[data-testid="chart-legend-item"]',
      ) || []),
    ]

    figure.style.margin = '0'
    chartContainer.style.width = chartContainer.offsetWidth * scale + PX
    chartLayout.style.height = chartLayout.offsetHeight * scale + PX
    chartWatermark.style.padding =
      parseFloat(window.getComputedStyle(chartWatermark).padding) * scale + PX

    setMarginAndPaddingAccordingToScale(titleContainer, scale)
    setMarginAndPaddingAccordingToScale(chartDecoration, scale)

    if (chartTitle) {
      chartTitle.style.fontSize =
        parseInt(window.getComputedStyle(chartTitle).fontSize) * scale + PX
      chartTitle.style.lineHeight =
        parseInt(window.getComputedStyle(chartTitle).lineHeight) * scale + PX
    }

    if (chartDescription) {
      chartDescription.style.fontSize =
        parseInt(window.getComputedStyle(chartDescription).fontSize) * scale +
        PX
      chartDescription.style.lineHeight =
        parseInt(window.getComputedStyle(chartDescription).lineHeight) * scale +
        PX
    }

    if (watermarkP) {
      watermarkP.style.fontSize =
        parseInt(window.getComputedStyle(watermarkP).fontSize) * scale + PX
      watermarkP.style.lineHeight =
        parseInt(window.getComputedStyle(watermarkP).lineHeight) * scale + PX
    }

    watermarkSVG.width.baseVal.value = watermarkSVG.width.baseVal.value * scale
    watermarkSVG.height.baseVal.value =
      watermarkSVG.height.baseVal.value * scale
    chartDecoration.style.width =
      chartDecoration.width.baseVal.value * scale + PX
    chartDecoration.style.height =
      chartDecoration.height.baseVal.value * scale + PX

    watermarkSVGG.style.scale = scale.toString()

    if (legendItems.length) {
      legendItems.forEach((item) => {
        const legendBullet = item?.querySelector('span')

        legendBullet.style.width = legendBullet.offsetWidth * scale + PX
        legendBullet.style.height = legendBullet.offsetHeight * scale + PX

        item.style.fontSize =
          parseInt(window.getComputedStyle(item).fontSize) * scale + PX
        item.style.lineHeight =
          parseInt(window.getComputedStyle(item).lineHeight) * scale + PX
      })
    }

    updateChart(i)

    setTimeout(() => {
      htmlToImage
        .toPng(chartContainer)
        .then((dataUrl) => {
          const link = document.createElement('a')
          link.download = 'chart-screenshot.png'
          link.href = dataUrl
          link.click()

          $rudderstack.trackDownloads({
            file_id: 'chart_download',
            file_name: `chart-${i}`,
            file_type: 'png',
            file_format: 'png',
          })
        })
        .catch(() => {
          alert('Error capturing graph:')
          emit('update:loadingChartGeneration', false)
        })

      setTimeout(() => {
        chartContainer.style.width = chartContainer.offsetWidth / scale + PX
        watermarkSVGG.style.scale = '1'
        chartData.key++
        chartFontSize.value = chartDefaultFontSize
        emit('update:loadingChartGeneration', false)
      }, 2000)
    }, 2000)
  }, 1000)
}
</script>

<template>
  <div>
    <div
      id="generating-overlay"
      class="fixed left-0 top-0 z-20 hidden h-screen w-screen items-center justify-center gap-2 bg-s-900 text-2xl font-bold text-white"
      :class="loadingChartGeneration && '!flex'"
    >
      Generating chart
      <LoadingSpinner class="grow-0 !py-0 !text-s-800" />
    </div>

    <div class="hidden">
      <div
        v-for="(chart, i) in chartsData"
        :ref="(el) => (charts[i] = el)"
        :key="i"
      >
        <div v-if="$config.public.isPreviewSite">
          <div class="multiselect-container flex items-center gap-2">
            <BaseButton
              class="flex-shrink-0 rounded-md bg-p-500 px-6 py-2 font-semibold hover:bg-p-700 active:bg-p-800"
              :loading="loadingChartGeneration"
              @click="captureScreenWithDomToImage(i, chart)"
            >
              Download Chart
            </BaseButton>
            <VueMultiselect
              id="scales"
              v-model="selectedScale"
              :options="allScales"
              placeholder="Select scale"
              class="max-w-[100px] rounded border-2 border-s-300"
            >
            </VueMultiselect>
          </div>
          <button
            class="mt-2 flex-shrink-0 rounded-md border border-p-500 px-6 py-2 font-semibold"
            :class="hideYAxis && 'bg-p-500'"
            @click="
              () => {
                hideYAxis = !hideYAxis
                chart.key++
              }
            "
          >
            Hide y axis
          </button>
        </div>

        <div :id="`chart-${i}`">
          <ChartBuilder
            :key="`${chart.key}-${hideYAxis}`"
            :data="chart"
            :axis-font-size="chartFontSize"
            container-classes="chart-container-layout h-fit min-h-[362px]"
            chart-classes="chart-layout h-[362px]"
            legend-classes="chart-legend-layout"
            :hide-y-axis="hideYAxis"
            @chart-ref="
              (chartRef) => {
                theRef(chartRef, i)
              }
            "
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.chart-layout {
  @apply mb-0 max-h-[363px] w-[calc(100%_-_.2rem)] md:px-4;
}

.chart-legend-layout {
  @apply shrink-0 px-2 py-4 md:pb-8 md:pl-14 md:pr-4;
}
</style>
